import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const WellnessSearchInterface = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [retreatType, setRetreatType] = useState('');
  const [dateRange, setDateRange] = useState({ checkIn: '', checkOut: '' });
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const locationRef = useRef(null);
  const dateRef = useRef(null);
  const navigate = useNavigate();

  const retreatTypes = [
    { value: '', label: 'All Types' },
    { value: 'ees-center', label: 'EES Centers' },
    { value: 'meditation-retreat', label: 'Meditation Retreats' },
    { value: 'spa-resort', label: 'Spa Resorts' },
    { value: 'yoga-retreat', label: 'Yoga Retreats' },
    { value: 'detox-center', label: 'Detox Centers' },
    { value: 'crystal-healing', label: 'Crystal Healing' }
  ];

  const locationSuggestions = [
    'Sedona, AZ - Energy Vortex',
    'Tulum, Mexico - Beach Wellness',
    'Rishikesh, India - Spiritual Hub',
    'Big Sur, CA - Nature Immersion',
    'Bali, Indonesia - Healing Paradise',
    'Costa Rica - Pura Vida Wellness',
    'Mount Shasta, CA - Sacred Energy',
    'Glastonbury, UK - Mystical Healing'
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchParams = new URLSearchParams();

    if (searchQuery) searchParams.append('query', searchQuery);
    if (location) searchParams.append('location', location);
    if (retreatType) searchParams.append('type', retreatType);
    if (dateRange.checkIn) searchParams.append('checkIn', dateRange.checkIn);
    if (dateRange.checkOut) searchParams.append('checkOut', dateRange.checkOut);

    navigate(`/wellness-center-listings?${searchParams.toString()}`);
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setIsLocationDropdownOpen(false);
  };

  const getDateRangeDisplay = () => {
    if (dateRange.checkIn && dateRange.checkOut) {
      const checkIn = new Date(dateRange.checkIn).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric'
      });
      const checkOut = new Date(dateRange.checkOut).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric'
      });
      return `${checkIn} - ${checkOut}`;
    }
    return 'Add dates';
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <form onSubmit={handleSearch} className="space-y-6">
        {/* Main Search Container */}
        <div className="bg-white rounded-2xl shadow-elevation-3 p-6 border border-border">
          {/* Primary Search Input */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                {/* Icon color changed from blue-500 to primary */}
                <Icon name="Search" size={24} className="text-primary" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for EES centers, meditation retreats, healing sanctuaries..."
                className="block w-full pl-16 pr-6 py-6 text-lg border-0 rounded-xl
                           focus:ring-2 focus:ring-primary focus:border-transparent
                           transition-all duration-200 ease-out bg-background-light text-text-primary
                           placeholder-text-secondary font-medium"
              />
            </div>
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Location Filter */}
            <div className="relative" ref={locationRef}>
              <button
                type="button"
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-4 bg-surface
                           border-2 border-border rounded-xl hover:border-border-focus
                           transition-all duration-200 ease-out text-left"
              >
                <div className="flex items-center space-x-3">
                  {/* Icon color changed from blue-600 to primary */}
                  <Icon name="MapPin" size={20} className="text-primary" />
                  <div>
                    <div className="text-xs text-text-secondary font-medium uppercase tracking-wide">
                      Location
                    </div>
                    <div className="text-sm font-medium text-text-primary">
                      {location || 'Where to?'}
                    </div>
                  </div>
                </div>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`text-text-secondary transition-transform duration-200 ${
                    isLocationDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isLocationDropdownOpen && (
                <div className="absolute top-full mt-2 w-full md:w-80 bg-surface rounded-xl
                                 shadow-elevation-4 border border-border z-dropdown">
                  <div className="p-4">
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Search destinations..."
                      className="w-full px-4 py-3 border border-border rounded-lg text-sm
                                 focus:border-primary focus:ring-2 focus:ring-background-medium"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {locationSuggestions
                      .filter(suggestion =>
                        suggestion.toLowerCase().includes(location.toLowerCase())
                      )
                      .map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => handleLocationSelect(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-background-light transition-colors duration-200 border-b border-border last:border-b-0"
                        >
                          <div className="flex items-center space-x-3">
                            {/* Icon color changed from blue-500 to primary */}
                            <Icon name="MapPin" size={16} className="text-primary" />
                            <span className="text-sm text-text-primary">{suggestion}</span>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Retreat Type Filter */}
            <div className="relative">
              <div className="w-full px-4 py-4 bg-surface border-2 border-border rounded-xl">
                <div className="flex items-center space-x-3">
                  {/* Icon color changed from blue-600 to primary */}
                  <Icon name="Sparkles" size={20} className="text-primary" />
                  <div className="flex-1">
                    <div className="text-xs text-text-secondary font-medium uppercase tracking-wide">
                      Experience
                    </div>
                    <select
                      value={retreatType}
                      onChange={(e) => setRetreatType(e.target.value)}
                      className="w-full text-sm font-medium text-text-primary bg-transparent border-0 focus:ring-0 p-0 appearance-none cursor-pointer"
                    >
                      {retreatTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Icon name="ChevronDown" size={16} className="text-text-secondary" />
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="relative" ref={dateRef}>
              <button
                type="button"
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="w-full flex items-center justify-between px-4 py-4 bg-surface
                           border-2 border-border rounded-xl hover:border-border-focus
                           transition-all duration-200 ease-out text-left"
              >
                <div className="flex items-center space-x-3">
                  {/* Icon color changed from blue-600 to primary */}
                  <Icon name="Calendar" size={20} className="text-primary" />
                  <div>
                    <div className="text-xs text-text-secondary font-medium uppercase tracking-wide">
                      Dates
                    </div>
                    <div className="text-sm font-medium text-text-primary">
                      {getDateRangeDisplay()}
                    </div>
                  </div>
                </div>
                <Icon name="ChevronDown" size={16} className="text-text-secondary" />
              </button>

              {isDatePickerOpen && (
                <div className="absolute top-full mt-2 w-full md:w-80 bg-surface rounded-xl
                                 shadow-elevation-4 border border-border z-dropdown p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Check-in
                      </label>
                      <input
                        type="date"
                        value={dateRange.checkIn}
                        onChange={(e) => setDateRange(prev => ({ ...prev, checkIn: e.target.value }))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-background-medium"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Check-out
                      </label>
                      <input
                        type="date"
                        value={dateRange.checkOut}
                        onChange={(e) => setDateRange(prev => ({ ...prev, checkOut: e.target.value }))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-background-medium"
                        min={dateRange.checkIn || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Search Button */}
            <button
              type="submit"
              className="flex items-center justify-center px-6 py-4 bg-gradient-to-r
                         from-primary to-secondary text-white font-semibold rounded-xl
                         hover:from-secondary hover:to-primary focus:ring-4 focus:ring-background-medium
                         transition-all duration-200 ease-out micro-interaction shadow-elevation-2"
            >
              <Icon name="Search" size={20} className="md:mr-2" />
              <span className="hidden md:inline">Search</span>
            </button>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-3 justify-center">
          {[
            { label: 'Energy Enhancement', icon: 'Zap' },
            { label: 'Meditation', icon: 'Heart' },
            { label: 'Chakra Balancing', icon: 'Sparkles' },
            { label: 'Sound Healing', icon: 'Headphones' },
            { label: 'Crystal Therapy', icon: 'Gem' },
            { label: 'Detox Programs', icon: 'Leaf' }
          ].map((filter) => (
            <button
              key={filter.label}
              type="button"
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-border
                         rounded-full hover:bg-background-light hover:border-border-focus transition-all
                         duration-200 ease-out text-sm font-medium text-primary"
            >
              <Icon name={filter.icon} size={14} />
              <span>{filter.label}</span>
            </button>
          ))}
        </div>
      </form>
    </div>
  );
};

export default WellnessSearchInterface;
